import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Iconify } from "@/lib/iconify";
import { UI_ICONS } from "@/lib/ui-icons";
import { useLocation, useRouter } from "@/lib/navigation";
import {
  dismissJobNotice,
  requestJobFocus,
  stopAllBackgroundJobs,
  subscribeJobNotices,
  type JobNotice,
} from "@/blyn/services/backgroundJobTracker";

// Same icons/colours as the inline step rows, so a failed build reads the
// same here as in the thread.
const KIND_STYLE: Record<JobNotice["kind"], { icon: string; color: string }> = {
  success: { icon: UI_ICONS.success, color: "var(--success-text)" },
  error:   { icon: UI_ICONS.error,   color: "var(--danger-text)" },
  warning: { icon: UI_ICONS.warning, color: "var(--yellow-text)" },
};

// Successes fade on their own; failures wait to be seen.
const SUCCESS_DISMISS_MS = 10_000;

/** Clickable notices for background builds (journey / campaign creates) that
 * finished while their message wasn't on screen. Clicking one opens the
 * conversation and scrolls to the message. Mounted once by the Blyn layout,
 * which also owns the tracker's lifetime. */
export function BackgroundJobNotices() {
  const [notices, setNotices] = useState<JobNotice[]>([]);
  const router = useRouter();
  const location = useLocation();

  useEffect(() => subscribeJobNotices(setNotices), []);
  // Leaving the Blyn app closes every progress connection.
  useEffect(() => stopAllBackgroundJobs, []);

  useEffect(() => {
    const timers = notices
      .filter((n) => n.kind === "success")
      .map((n) => setTimeout(() => dismissJobNotice(n.id), SUCCESS_DISMISS_MS));
    return () => timers.forEach(clearTimeout);
  }, [notices]);

  if (notices.length === 0) return null;

  const open = (notice: JobNotice) => {
    dismissJobNotice(notice.id);
    requestJobFocus({ conversationId: notice.conversationId, messageIndex: notice.messageIndex });
    const onConversation =
      location.pathname.endsWith("/new-conversation") &&
      new URLSearchParams(location.search).get("id") === notice.conversationId;
    if (!onConversation) router.push(`/blyn/new-conversation?id=${encodeURIComponent(notice.conversationId)}`);
  };

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, display: "flex", flexDirection: "column", gap: 1, maxWidth: 340 }}
    >
      {notices.map((notice) => {
        const style = KIND_STYLE[notice.kind];
        return (
          <Box
            key={notice.id}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              p: "10px 12px",
              borderRadius: "var(--r-sm)",
              border: "1px solid var(--line)",
              borderLeft: `3px solid ${style.color}`,
              bgcolor: "var(--bg-canvas)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => open(notice)}
              title="Go to message"
              sx={{
                all: "unset",
                cursor: "pointer",
                display: "flex",
                gap: "10px",
                flex: 1,
                minWidth: 0,
                "&:focus-visible": { outline: "2px solid var(--accent-text)", outlineOffset: 2 },
              }}
            >
              <Box component="span" sx={{ display: "flex", color: style.color, mt: "1px" }}>
                <Iconify icon={style.icon} width={16} />
              </Box>
              <Box component="span" sx={{ minWidth: 0, fontSize: 12.5, lineHeight: 1.45 }}>
                <Box component="span" sx={{ display: "block", fontWeight: 600, color: "text.primary" }}>{notice.title}</Box>
                {notice.detail && (
                  <Box component="span" sx={{ display: "block", color: "text.secondary", wordBreak: "break-word" }}>{notice.detail}</Box>
                )}
                <Box component="span" sx={{ display: "block", mt: "4px", color: "var(--accent-text)", fontWeight: 500 }}>View in conversation</Box>
              </Box>
            </Box>
            <Box
              component="button"
              type="button"
              aria-label="Dismiss"
              onClick={() => dismissJobNotice(notice.id)}
              sx={{ all: "unset", cursor: "pointer", display: "flex", color: "text.secondary", "&:hover": { color: "text.primary" } }}
            >
              <Iconify icon={UI_ICONS.close} width={14} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
